import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Printer, Loader2, BarChart3, Quote, Target, Zap, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="text-3xl font-bold text-amber-700">{value}</div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">
        {label}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function InvestorOnePager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/investor/one-pager`, {
          withCredentials: true,
        });
        setData(res.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Failed to load investor data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-red-600">{error}</p>
        <p className="text-sm text-muted-foreground mt-2">
          You must be an admin to view this page.
        </p>
      </div>
    );
  }

  const m = data.traction;

  return (
    <div
      className="container mx-auto px-4 py-10 max-w-5xl print:py-4 print:max-w-none"
      data-testid="investor-one-pager"
    >
      {/* Toolbar (hidden on print) */}
      <div className="flex justify-end mb-4 print:hidden">
        <Button onClick={() => window.print()} data-testid="print-btn">
          <Printer className="w-4 h-4 mr-2" />
          Print / Save as PDF
        </Button>
      </div>

      {/* Header */}
      <header className="border-b-4 border-amber-500 pb-6 mb-8">
        <h1 className="text-4xl sm:text-5xl font-heading font-bold">{data.headline}</h1>
        <p className="text-lg text-muted-foreground mt-2">{data.tagline}</p>
        <a
          href={data.public_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-amber-700 hover:underline"
        >
          {data.public_url}
        </a>
      </header>

      {/* Problem / Solution */}
      <section className="grid md:grid-cols-2 gap-6 mb-10">
        <Card>
          <CardContent className="p-5">
            <h2 className="font-heading text-lg mb-2 flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-600" />
              Problem
            </h2>
            <p className="text-sm leading-relaxed">{data.problem}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <h2 className="font-heading text-lg mb-2 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-600" />
              Solution
            </h2>
            <p className="text-sm leading-relaxed">{data.solution}</p>
          </CardContent>
        </Card>
      </section>

      {/* Traction */}
      <section className="mb-10">
        <h2 className="font-heading text-xl mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-amber-600" />
          Live Traction Snapshot
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Stat label="Total Members" value={m.members.total.toLocaleString()} />
          <Stat
            label="New (30d)"
            value={m.members.new_30d.toLocaleString()}
            sub={`${m.members.new_7d} in last 7d`}
          />
          <Stat
            label="Paid Conversion"
            value={`${m.members.conversion_rate_pct}%`}
            sub={`${m.members.paid} paid members`}
          />
          <Stat
            label="Premium Tier"
            value={`${m.members.premium_rate_pct}%`}
            sub={`${m.members.premium} premium`}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <Stat label="Posts (30d)" value={m.content_velocity_30d.posts} />
          <Stat label="Blog articles (30d)" value={m.content_velocity_30d.blog_articles} />
          <Stat label="Events (30d)" value={m.content_velocity_30d.events} />
          <Stat label="Breeds indexed" value={m.catalog.breeds} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Dog Parks" value={m.catalog.parks} />
          <Stat label="AADC Clubs" value={m.catalog.clubs} />
          <Stat label="Research Institutions" value={m.catalog.research_institutions} />
          <Stat
            label="Referrals"
            value={m.referrals.codes_created}
            sub={`${m.referrals.referrers_with_conversions} converting`}
          />
        </div>
      </section>

      {/* Monetization + Moat */}
      <section className="grid md:grid-cols-2 gap-6 mb-10">
        <Card>
          <CardContent className="p-5">
            <h2 className="font-heading text-lg mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-600" />
              Monetization
            </h2>
            <ul className="space-y-2 text-sm">
              {data.monetization.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="text-amber-600">▸</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <h2 className="font-heading text-lg mb-3 flex items-center gap-2">
              <Quote className="w-5 h-5 text-amber-600" />
              Defensibility
            </h2>
            <ul className="space-y-2 text-sm">
              {data.moat.map((item) => (
                <li key={item} className="flex gap-2">
                  <span className="text-amber-600">▸</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* Ask + Team */}
      <section className="grid md:grid-cols-2 gap-6 mb-10">
        <div className="border-l-4 border-amber-500 pl-4">
          <h3 className="font-heading text-base text-muted-foreground">The Ask</h3>
          <p className="mt-1 text-sm italic">{data.ask}</p>
        </div>
        <div className="border-l-4 border-amber-500 pl-4">
          <h3 className="font-heading text-base text-muted-foreground">Team</h3>
          <p className="mt-1 text-sm italic">{data.team}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center text-xs text-muted-foreground border-t pt-4">
        Generated {new Date(data.generated_at).toLocaleString()} · Contact: {data.contact}
      </footer>

      <style>{`
        @media print {
          body { font-size: 11pt; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
